<template>
  <div
    v-tooltip.html.placementBottom="tooltip"
    class="
      tw-group
      tw-relative
      tw-px-3
      tw-py-2.5
      tw-border
      tw-border-solid
      tw-rounded
      tw-border-sf-snow-dark
      tw-min-w-[40px]
      tw-max-w-[240px]
      tw-cursor-pointer
      hover:tw-bg-sf-snow
    "
    :data-filter-type="filterDefinition.type"
    :class="{ 'tw-opacity-50 !tw-cursor-not-allowed': isDisabled }"
    @click="editFilter()"
  >
    <span class="tw-flex tw-items-center tw-gap-2 tw-truncate">
      <span class="tw-truncate">{{
        filterTitle(filterDefinition.nameKey)
      }}</span>
      <span
        v-if="isNew"
        class="
          tw-shrink-0
          tw-bg-sf-primary-subtle
          tw-rounded-full
          tw-px-2
          tw-py-1
          tw-text-sf-primary-dark
          tw-text-[11px]
          tw-uppercase
          tw-leading-none
        "
      >
        {{ I18n.t('companies_overview.filters.new_badge') }}
      </span>
    </span>

    <div
      v-if="!isDisabled"
      class="
        tw-pointer-events-none
        tw-absolute
        tw-inset-y-0
        tw-right-0
        tw-flex
        tw-w-14
        tw-items-center
        tw-justify-end
        tw-pr-2
        tw-opacity-0
        group-hover:tw-opacity-100
        tw-bg-[linear-gradient(90deg,_rgba(245,245,245,0)_0%,_rgba(245,245,245,1)_50%)]
      "
    >
      <span
        class="
          tw-pointer-events-auto
          tw-flex
          tw-h-5
          tw-w-5
          tw-items-center
          tw-justify-center
          tw-rounded
          tw-text-sf-gray-dark
        "
      >
        <i
          v-if="!isDisabled"
          class="fa tw-text-sm tw-leading-none tw-text-sf-primary-dark"
          :class="iconClass"
        />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { FilterDefinition } from '@app/insights/types'
import { filterTitle, filterModalHelp } from '@app/insights/utils/filter-texts'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useFiltersStore } from '@app/insights/store/filters'
import { useInsightsRootStore } from '@app/insights/store/insights-root'

defineOptions({ name: 'AddFilterItem' })

const props =
  defineProps<{
    filterDefinition: FilterDefinition
    liteMode?: boolean
  }>()

const emit =
  defineEmits<{
    editFilter: []
  }>()

// Composables
const I18n = useI18n()
const rootStore = useInsightsRootStore()
const filtersStore = useFiltersStore()

// Computed
const isDisabled = computed(
  () => !!props.filterDefinition.disabledOnFirmLevel?.()
)

const isNew = computed(() => {
  const { deployDate } = props.filterDefinition
  if (!deployDate) return false
  const daysSinceDeploy =
    (Date.now() - new Date(deployDate).getTime()) / (1000 * 60 * 60 * 24)
  return daysSinceDeploy < 30
})

const filterHelp = computed(() => {
  let helpText = filterModalHelp(props.filterDefinition.nameKey)
  if (props.liteMode) {
    helpText += `<br />---<br /> ${I18n.t(
      'companies_overview_lite.premium_tooltip'
    )}`
  }
  return helpText
})
const tooltip = computed(() =>
  isDisabled.value
    ? I18n.t('companies_overview.filters.feature_flag_disabled')
    : filterHelp.value || undefined
)

const iconClass = computed(() => (props.liteMode ? 'fa-gem' : 'fa-plus'))

// Handlers
function editFilter() {
  if (isDisabled.value) {
    return
  }

  if (props.liteMode) {
    rootStore.togglePremiumModal()
  } else {
    filtersStore.editFilter(props.filterDefinition as Record<string, unknown>)
  }
  emit('editFilter')
}
</script>
